import { LineSeries } from '@nivo/line';

export enum EDashboardItemType {
  SPOT = 'spot',
  FORWARDS = 'forwards',
  LISTED_SPOT = 'listed_spot',
  LISTED_FORWARD = 'listed_forward',
  REVENUE = 'revenue',
  EXPENSE = 'expense',
}

export enum DashboardChartBarDataType {
  TYPE_OF_SUPPORT = 'Type of Support',
  AIB_GRID_CONNECTED = 'AIB Grid Connected',
  SPOT = 'Spot',
  FORWARD = 'Forward',
}

export enum EDashboardTabType {
  SPOT = 'spot',
  FORWARD = 'forward',
  REVENUE_EXPENSE = 'revenue_expense',
  PREDICTIONS = 'predictions',
}

export enum EDashboardChartRange {
  WEEK = 'week',
  MONTH = 'month',
  SIX_MONTHS = 'six_months',
  YEAR = 'year',
}

export enum EDashboardChartDataType {
  VOLUME = 'chart_volume',
  PRICE = 'chart_price',
  TYPE_OF_INSTALLATION = 'type_of_installation',
  ENERGY_SOURCE = 'energy_source',
  AIB_GRID_CONNECTED = 'aib_grid_connected',
  TYPE_OF_SUPPORT = 'type_of_support',
  DETAILS = 'details',
  ACQUIRED = 'acquired',
  SCHEDULED = 'scheduled',
  REVENUE = 'chart_revenue',
  EXPENSE = 'chart_expense',
  SPOT = 'chart_spot',
  FORWARD = 'chart_forward',
  PREDICTIONS_PERCENTAGE = 'predictions_percentage',
}

export interface IDashboardChartDataWithDate {
  date: string;
  value: number;
}

export interface IDashboardChartDataWithName {
  name: string;
  value: number;
  additional_value?: number;
}

export interface IDashboardRevenueExpenseChartDataResponse {
  revenue: number;
  expense: number;
}

export interface IDashboardChartLineDataResponse {
  diff: number;
  data: IDashboardChartDataWithDate[];
}

export interface IDashboardChartLineData {
  diff: number;
  data: LineSeries[];
  range: EDashboardChartRange | string;
}

export type ChartBarKey = 'firstVolume' | 'secondVolume';

export interface IDashboardSingleChartBarData {
  type: string;
  firstVolume: number;
  firstVolumeTitle: string;
  firstVolumeColor: string;
  firstVolumeBorderColor: string;
  firstVolumeFilterValue: string;
}

export interface IDashboardDetailsChartBarData extends IDashboardSingleChartBarData {
  secondVolume: number;
  secondVolumeTitle: string;
  secondVolumeColor: string;
  secondVolumeBorderColor: string;
  secondVolumeFilterValue: string;
}

export interface IDashboardChartPieData {
  id: string;
  label: string;
  value: number;
  formattedValue?: number | string;
  color: string;
}

export interface IDashboardSpotStaticChartData {
  [EDashboardChartDataType.TYPE_OF_INSTALLATION]: IDashboardChartPieData[];
  [EDashboardChartDataType.ENERGY_SOURCE]: IDashboardChartPieData[];
  [EDashboardChartDataType.DETAILS]: IDashboardDetailsChartBarData[];
}

export interface IDashboardForwardStaticChartData extends IDashboardSpotStaticChartData {
  [EDashboardChartDataType.ACQUIRED]: IDashboardChartPieData[];
  [EDashboardChartDataType.SCHEDULED]: IDashboardChartPieData[];
}

export interface IDashboardRevenueExpenseStaticChartData {
  [EDashboardChartDataType.REVENUE]: IDashboardSingleChartBarData[];
  [EDashboardChartDataType.EXPENSE]: IDashboardSingleChartBarData[];
}

export interface IDashboardPredictionsStaticChartData {
  [EDashboardChartDataType.PREDICTIONS_PERCENTAGE]: IDashboardChartPieData[];
}

export interface IDashboardPredictionsStaticChartDataResponse {
  [EDashboardChartDataType.PREDICTIONS_PERCENTAGE]: IDashboardChartDataWithName[];
}

export interface IDashboardVolumeChartResponse {
  [EDashboardChartDataType.VOLUME]: IDashboardChartLineDataResponse;
}

export interface IDashboardRevenueExpenseLineChartResponse {
  [EDashboardChartDataType.REVENUE]: IDashboardChartLineDataResponse;
  [EDashboardChartDataType.EXPENSE]: IDashboardChartLineDataResponse;
}

export interface IDashboardRevenueExpenseStaticChartResponse {
  [EDashboardChartDataType.SPOT]: IDashboardRevenueExpenseChartDataResponse;
  [EDashboardChartDataType.FORWARD]: IDashboardRevenueExpenseChartDataResponse;
}

export interface IDashboardExchangeChartResponse extends IDashboardVolumeChartResponse {
  [EDashboardChartDataType.PRICE]: IDashboardChartLineDataResponse;
}

export interface IDashboardStaticSpotChartResponse {
  [EDashboardChartDataType.TYPE_OF_INSTALLATION]: IDashboardChartDataWithName[];
  [EDashboardChartDataType.ENERGY_SOURCE]: IDashboardChartDataWithName[];
  [EDashboardChartDataType.AIB_GRID_CONNECTED]: IDashboardChartDataWithName[];
  [EDashboardChartDataType.TYPE_OF_SUPPORT]: IDashboardChartDataWithName[];
}

export interface IDashboardStaticForwardChartResponse extends IDashboardStaticSpotChartResponse {
  [EDashboardChartDataType.ACQUIRED]: IDashboardChartDataWithName[];
  [EDashboardChartDataType.SCHEDULED]: IDashboardChartDataWithName[];
}

export interface IDashboardChartPayload {
  range?: EDashboardChartRange;
  year?: number | string;
}

export interface EDashboardChartLegend {
  title: string;
  color: string;
}

export interface IDashboardItemInfo {
  title: string;
  total: number | null;
  change: number | null;
  url: string;
}

export interface IDashboardItem {
  [EDashboardItemType.SPOT]: IDashboardItemInfo;
  [EDashboardItemType.FORWARDS]: IDashboardItemInfo;
  [EDashboardItemType.LISTED_FORWARD]: IDashboardItemInfo;
  [EDashboardItemType.LISTED_SPOT]: IDashboardItemInfo;
  [EDashboardItemType.REVENUE]: IDashboardItemInfo;
  [EDashboardItemType.EXPENSE]: IDashboardItemInfo;
}

export interface FetchDashboardResponse {
  [EDashboardItemType.SPOT]: Pick<IDashboardItemInfo, 'total' | 'change'>;
  [EDashboardItemType.FORWARDS]: Pick<IDashboardItemInfo, 'total' | 'change'>;
  [EDashboardItemType.LISTED_FORWARD]: Pick<IDashboardItemInfo, 'total' | 'change'>;
  [EDashboardItemType.LISTED_SPOT]: Pick<IDashboardItemInfo, 'total' | 'change'>;
  [EDashboardItemType.REVENUE]: Pick<IDashboardItemInfo, 'total' | 'change'>;
  [EDashboardItemType.EXPENSE]: Pick<IDashboardItemInfo, 'total' | 'change'>;
}
